import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getBrand } from "assets/js/data/branding/actions";
import CakeStore from "./store";
import { Provider } from "react-redux";
import { bindActionCreators } from "redux";
import Cookies from "universal-cookie";
import TagManager from "cakemail-react-gtm-module";
import { setLocaleWithFallback } from "utils/languages";
import originalBrand from "assets/js/scenes/brand/data/originalBrand";
import { GATEWAY_PROXY } from "utils/apiUtils";
import {
	getUserBrandSDK,
	getUserBrandService,
} from "assets/js/services/brandingService";
import { getBrandImage, getUrlQueryParam } from "utils/urlUtils";
import {
	setPublicUserBrand,
	setPublicUserBrandRepo,
} from "assets/js/data/publicFacingBranding/actions";
import { initPostHog } from "assets/js/const/SegmentEvents";
import { FEATURE_FLAG, hasFeatureFlag } from "./utils/LimitsAndFeatures";

const BrandWrapper = (props) => {
	return (
		<Provider store={CakeStore}>
			<ConnectedBrand>{props.children}</ConnectedBrand>
		</Provider>
	);
};

BrandWrapper.propTypes = {
	children: PropTypes.any,
};

export default BrandWrapper;

export function setBrandHeadElements(data) {
	var link = document.getElementById("favicon");
	if (link) link.href = getBrandImage("favicon", data);
	link = document.getElementById("favicon-apple-touch");
	if (link) link.href = getBrandImage("faviconAppleTouch", data);
	link = document.getElementById("favicon-16");
	if (link) link.href = getBrandImage("favicon16", data);
	link = document.getElementById("favicon-32");
	if (link) link.href = getBrandImage("favicon32", data);
	link = document.getElementById("favicon-512");
	if (link) link.href = getBrandImage("faviconAndroidChrome512", data);
	link = document.getElementById("favicon-mstile");
	if (link) link.href = getBrandImage("faviconMstile", data);

	document.title = data.name;
}

export function getBrandCallback(data) {
	const cookies = new Cookies();
	cookies.set("app_hostname", window.location.hostname, { path: "/" });

	//Set Original brand data on completely missing brand data
	if (!data.name) {
		data.name = "";
	}
	if (!data?.images) {
		data.images = originalBrand.images;
	}
	if (!data?.url) {
		data.url = originalBrand.url;
	}
	if (!data?.theme) {
		data.theme = originalBrand.theme;
	}
	if (!data?.theme?.base) {
		data.theme.base = originalBrand.theme.base;
	}
	if (!data?.theme?.advance) {
		data.theme.advance = originalBrand.theme.advance;
	}

	setBrandHeadElements(data);

	const localCookie = cookies.get("locale");

	if (data?.client_config?.default_language && !localCookie) {
		setLocaleWithFallback(data?.client_config?.default_language, data?.client_config?.languages);
	}

	if (data?.client_config?.google_tag_manager_id) {
		let config = {
			gtmId: data.client_config.google_tag_manager_id,
		};

		if (data?.client_config?.google_tag_manager_server) {
			config.server = data.client_config.google_tag_manager_server;
		}

		TagManager.initialize(config);
	}

	if (data?.client_config?.segment_config?.enabled && !data?.client_config?.segment_config?.disabled_events) {
		data.client_config.segment_config.disabled_events = [];
	}

	if (
		data?.client_config?.profitwell_config?.id &&
		data?.client_config?.profitwell_config?.enabled
	) {
		injectProfitwell(data?.client_config?.profitwell_config?.id);
	}

	if (hasFeatureFlag(FEATURE_FLAG.VERISOUL, data)) {
		injectVerisoul(data?.client_config?.verisoul);
	}

	// Fallbacks
	if (!data.images["illAddContacts"]) {
		data.images["illAddContacts"] =
			originalBrand.images["ill-add-contacts"];
	}

	if (!data.images["illWorkflow"]) {
		data.images["illWorkflow"] = originalBrand.images["ill-workflow"];
	}

	// empty states
	if (!data.images["illEmptyAutomations"]) {
		data.images["illEmptyAutomations"] =
			originalBrand.images["ill-empty-automations"];
	}
	if (!data.images["illEmptyCampaignsDrafts"]) {
		data.images["illEmptyCampaignsDrafts"] =
			originalBrand.images["ill-empty-campaigns-drafts"];
	}
	if (!data.images["illEmptyCampaignsOutbox"]) {
		data.images["illEmptyCampaignsOutbox"] =
			originalBrand.images["ill-empty-campaigns-outbox"];
	}
	if (!data.images["illEmptyCampaignsSent"]) {
		data.images["illEmptyCampaignsSent"] =
			originalBrand.images["ill-empty-campaigns-sent"];
	}
	if (!data.images["illEmptyCampaignsTemplates"]) {
		data.images["illEmptyCampaignsTemplates"] =
			originalBrand.images["ill-empty-campaigns-templates"];
	}
	if (!data.images["illEmptyContacts"]) {
		data.images["illEmptyContacts"] =
			originalBrand.images["ill-empty-contacts"];
	}
	if (!data.images["illEmptyListing"]) {
		data.images["illEmptyListing"] =
			originalBrand.images["ill-empty-listing"];
	}
	if (!data.images["illEmptyForms"]) {
		data.images["illEmptyForms"] =
			originalBrand.images["ill-empty-forms"];
	}
	if (!data.images["illEmptyAbTestOutbox"]) {
		data.images["illEmptyAbTestOutbox"] =
			originalBrand.images["ill-empty-ab-test-outbox"];
	}
	if (!data.images["illEmptyAbTestDrafts"]) {
		data.images["illEmptyAbTestDrafts"] =
			originalBrand.images["ill-empty-ab-test-drafts"];
	}
	if (!data.images["illEmptyAbTestComplete"]) {
		data.images["illEmptyAbTestComplete"] =
			originalBrand.images["ill-empty-ab-test-complete"];
	}

	return data;
}

const Brand = (props) => {
	useEffect(() => {
		const userBrandOwner = getUrlQueryParam("a");

		if (userBrandOwner) {
			getUserBrandService({ id: userBrandOwner }, (userBrand) => {
				if (userBrand?.client_config?.brand_repo?.url) {
					getUserBrandSDK(
						{ url: userBrand?.client_config?.brand_repo?.url },
						(userBrandSDK) => {
							CakeStore.dispatch(setPublicUserBrandRepo(userBrandSDK));
						}
					);
				}
				CakeStore.dispatch(setPublicUserBrand(userBrand));
				initPostHog(userBrand?.client_config?.segment_config);
			});
		}

		props.getBrand(getBrandCallback);
	}, []);
	return props.brand ? props.children : null;
};

export const ConnectedBrand = connect(
	(state) => {
		return {
			brand: state.brand,
		};
	},
	(dispatch) =>
		bindActionCreators(
			{
				getBrand,
			},
			dispatch
		)
)(Brand);

function injectProfitwell(id) {
	var profitWellScript = document.createElement("script");
	profitWellScript.innerHTML =
		"(function(i,s,o,g,r,a,m){i[o]=i[o]||function(){(i[o].q=i[o].q||[]).push(arguments)};a=s.createElement(g);m=s.getElementsByTagName(g)[0];a.async=1;a.src=r+'?auth='+ s.getElementById(o+'-js').getAttribute('data-pw-auth');m.parentNode.insertBefore(a,m);})(window,document,'profitwell','script','https://public.profitwell.com/js/profitwell.js');";
	profitWellScript.id = "profitwell-js";
	profitWellScript.setAttribute("data-pw-auth", id);
	document.head.appendChild(profitWellScript);
}


function injectVerisoul(config) {
	if (config && config.key) {
		var profitWellScript = document.createElement("script");
		profitWellScript.src = `https://js.verisoul.ai/${process.env.VERISOULS_ENV}/bundle.js`;
		profitWellScript.setAttribute("verisoul-project-id", config.key);
		document.head.appendChild(profitWellScript);
	}
}
